import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  sliceKey,
  reducer,
  dealerNurturingAutomationSettingsActions,
} from './slice';
import { watchDealerNurturingAutomationSettingsSaga } from './saga';
import { selectDealerNurturingAutomationSettings } from './selectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { initializeDealerNurturingAutomationSettings } from './data/dealerNurturingAutomationSettingsDataService';
import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';
import { ConfigureInitializeState } from 'app/components/ReusableUIComponents/ConfigureInitializeState';

import {
  Typography,
  Box,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const DEFAULT_PROPERTIES = {
  enable_nurturing_automation: false,
};

type DealerNurturingAutomationSettingsComponentProps = {
  defaultExpanded?: boolean;
};

export const DealerNurturingAutomationSettingsComponent = ({
  defaultExpanded = false,
}: DealerNurturingAutomationSettingsComponentProps) => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({
    key: sliceKey,
    saga: watchDealerNurturingAutomationSettingsSaga,
  });

  const dispatch = useDispatch();
  const selectedDealerId = useSelector(selectDealerSelected)?.dealer || '';
  const settingsState = useSelector(selectDealerNurturingAutomationSettings);
  const settingsBody = settingsState.data?.body || [];
  const loading = settingsState.loading;

  const [properties, setProperties] = useState({ ...DEFAULT_PROPERTIES });
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(false);

  const hasError = (() => {
    if (!settingsState.data) return false;
    const statusText = settingsState.data?.statusText;
    const statusCode =
      settingsState.data?.statusCode ??
      settingsState.data?.myAutoIQ_status_code ??
      settingsState.data?.errorCode;

    return (
      (statusText && statusText !== 'success') ||
      (typeof statusCode === 'number' && statusCode >= 400)
    );
  })();

  useEffect(() => {
    resetLocalState();
    if (!selectedDealerId) return;
    dispatch(
      dealerNurturingAutomationSettingsActions.requestDealerNurturingAutomationSettings(
        { dealershipId: selectedDealerId },
      ),
    );
  }, [selectedDealerId, dispatch]);

  useEffect(() => {
    loadSettingsFromStore();
  }, [settingsBody]);

  const resetLocalState = () => {
    setProperties({ ...DEFAULT_PROPERTIES });
    setSettingsId(null);
  };

  const loadSettingsFromStore = () => {
    try {
      if (!settingsBody.length) {
        resetLocalState();
        return;
      }

      const block = settingsBody.find(
        (settings: { _id?: string }) =>
          settings._id === 'DEALER_NURTURING_AUTOMATION',
      );
      const row = block?.settings?.[0];

      if (row?._id && row.properties) {
        setSettingsId(row._id);
        setProperties({
          enable_nurturing_automation:
            row.properties.enable_nurturing_automation === true,
        });
      } else {
        setSettingsId(null);
        setProperties({ ...DEFAULT_PROPERTIES });
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleSave = () => {
    if (!selectedDealerId || !settingsId || loading) return;

    const data = {
      type: 'DEALER_NURTURING_AUTOMATION',
      name: 'Nurturing Automation Settings',
      properties: {
        enable_nurturing_automation:
          properties.enable_nurturing_automation === true,
      },
    };

    dispatch(
      dealerNurturingAutomationSettingsActions.requestUpdateDealerNurturingAutomationSettings(
        {
          dealershipId: selectedDealerId,
          data,
          settingsId,
        },
      ),
    );
  };

  const handleInitialize = async () => {
    if (!selectedDealerId || initializing) return;
    setInitializing(true);
    try {
      const response = await initializeDealerNurturingAutomationSettings({
        dealerId: selectedDealerId,
      });
      const statusText = response?.statusText;
      if (statusText === 'success') {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message:
              'Dealer nurturing automation settings initialized successfully',
          }),
        );
        dispatch(
          dealerNurturingAutomationSettingsActions.requestDealerNurturingAutomationSettings(
            { dealershipId: selectedDealerId },
          ),
        );
      } else {
        throw new Error(response?.message || 'Failed to initialize settings');
      }
    } catch (error: any) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message:
            error?.message ||
            'Failed to initialize dealer nurturing automation settings',
        }),
      );
    } finally {
      setInitializing(false);
    }
  };

  const handleError = (error: unknown) => {
    console.log(error);
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: 'Oops! something went wrong',
      }),
    );
  };

  const noValidData = !settingsBody.length || !settingsId;
  const showInitializeButton =
    !loading && selectedDealerId && (hasError || noValidData);

  return (
    <AccordionComponent
      defaultExpanded={defaultExpanded}
      elevation={0}
      data-guidance-tour="configure-nurturing-automation-page"
      sx={{
        '&::before': {
          display: 'none',
        },
        padding: '16px',
      }}
    >
      <AccordionSummaryComponent
        expandIcon={<ExpandMoreIcon />}
        aria-controls="dealer-nurturing-automation-settings-content"
        id="dealer-nurturing-automation-settings-header"
      >
        <Typography variant="h6" gutterBottom component="div">
          Nurturing Automation
        </Typography>
      </AccordionSummaryComponent>
      <AccordionDetailsComponent>
        {showInitializeButton && (
          <ConfigureInitializeState
            warningMessage={
              hasError && settingsState.data?.message
                ? settingsState.data.message
                : null
            }
            errorMessage={settingsState.error ?? null}
            message="No nurturing automation config found. Click Initialize to set up."
            onInitialize={handleInitialize}
            initializing={initializing}
            buttonLabel="Initialize Nurturing Automation Settings"
          />
        )}

        {!showInitializeButton && (
          <Box>
            <FormControlLabel
              sx={{ display: 'flex' }}
              control={
                <Switch
                  checked={properties.enable_nurturing_automation === true}
                  onChange={() =>
                    setProperties(prev => ({
                      ...prev,
                      enable_nurturing_automation:
                        !prev.enable_nurturing_automation,
                    }))
                  }
                  inputProps={{ 'aria-label': 'Enable Nurturing Automation' }}
                />
              }
              label="Enable Nurturing Automation"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || !settingsId}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetailsComponent>
    </AccordionComponent>
  );
};
